import {
  CollectionCaseActionTypeCompositeEntity,
  CollectionCaseCompositeEntity,
  CompanyData,
} from '@nk/data-entities-lib';
import { includes } from '@nk/utils-lib';
import * as dayjs from 'dayjs';
import * as Highcharts from 'highcharts';
import { AccountsNumberPipe } from '../pipes/accounts-number.pipe';

/**
 * Payment remarks are only available for Norwegian companies that are not ENK.
 */
export function isPaymentRemarksEligible(companyData: CompanyData): boolean {
  return companyData.company.org_nr_schema === 'NO' && companyData.companyDetails?.organization_type_code !== 'ENK';
}

export interface CollectionCaseCompositeData {
  quantity: number;
  sum: number;
  quantityWithoutCreditors: number;
  period: string;
  collectionCaseCompositeEntities: CollectionCaseCompositeEntity[];
}

export interface CompanyPaymentRemarkTableItem {
  uuid: string;
  date: Date | null;
  sum: number;
  creditor: string;
  source: string;
  caseNumber: string;
  statuses: string[];
}

function getYearsRange(collectionCaseCompositeEntities: CollectionCaseCompositeEntity[] = []): string {
  const years = collectionCaseCompositeEntities
    .map((i) => i.collection_case.collection_reg_date?.getFullYear())
    .filter((y): y is number => y != null)
    .sort();
  if (!years.length) {
    return '';
  }
  return `${Math.min(...years)} - ${Math.max(...years)}`;
}

function getPaymentRemarksSum(collectionCaseCompositeEntities: CollectionCaseCompositeEntity[] = []): number {
  return collectionCaseCompositeEntities.reduce((res, i) => {
    return res + i.collection_case.latest_debt_amount;
  }, 0);
}

function getQuantityWithoutCreditors(collectionCaseCompositeEntities: CollectionCaseCompositeEntity[] = []): number {
  const uniqueKeys = new Set(collectionCaseCompositeEntities.map((i) => i.collection_case.creditor_name));
  return [...Array.from(uniqueKeys)].length;
}

export function getCollectionCaseCompositeData(
  response: CollectionCaseCompositeEntity[],
): CollectionCaseCompositeData | null {
  if (!response) {
    return null;
  }
  return {
    quantity: response.length,
    sum: getPaymentRemarksSum(response),
    quantityWithoutCreditors: getQuantityWithoutCreditors(response),
    period: getYearsRange(response),
    collectionCaseCompositeEntities: response,
  };
}

function getValuesByMonths(collectionCaseCompositeEntities: CollectionCaseCompositeEntity[] = []): {
  [date: string]: CollectionCaseCompositeEntity[];
} {
  return collectionCaseCompositeEntities.reduce((res: { [date: string]: CollectionCaseCompositeEntity[] }, i) => {
    const key = dayjs(i.collection_case.collection_reg_date).format('YYYY-MM');
    if (!res[key]) {
      res[key] = [];
    }
    res[key].push(i);
    return res;
  }, {});
}

/*
 * We are not allowed to show data older than 4 years from the latest payment remarks
 */
const ALLOWED_LOOKBACK_MONTHS = 48;

export function getMonthsRange(collectionCaseCompositeEntities: CollectionCaseCompositeEntity[] = []): string[] {
  if (collectionCaseCompositeEntities.length < 2) {
    return [];
  }
  const datesSorted = collectionCaseCompositeEntities
    .map((i) => i.collection_case.collection_reg_date)
    .filter((d): d is Date => d != null)
    .sort((a, b) => a.getTime() - b.getTime());
  if (datesSorted.length < 2) {
    return [];
  }
  const first = datesSorted[0];
  const last = datesSorted[datesSorted.length - 1];

  const earliestMonth = dayjs(first).format('YYYY-MM');
  const dates: string[] = [];
  for (let i = 0; i < ALLOWED_LOOKBACK_MONTHS; i++) {
    const currentMonth = dayjs(last).subtract(i, 'month').format('YYYY-MM');
    dates.push(currentMonth);
    if (currentMonth === earliestMonth) {
      break;
    }
  }
  return dates.reverse();
}

function calculateBarValue(collectionCaseCompositeEntities: CollectionCaseCompositeEntity[] = []): number {
  return collectionCaseCompositeEntities.reduce((res, i) => {
    return res + i.collection_case.latest_debt_amount;
  }, 0);
}

export function filterRelevantActionTypes(
  collectionCaseActionTypeComposites: CollectionCaseActionTypeCompositeEntity[],
): string[] {
  const statusesToInclude = ['nothing_for_distraint', 'partially_settled'];

  return Array.from(
    new Set(
      collectionCaseActionTypeComposites
        .map((i) => i.collection_case_action_type_group.collection_case_action_type_group_key)
        .filter((a) => includes(statusesToInclude, a)),
    ),
  );
}

function getCompanyPaymentRemarkStatus(item: CollectionCaseCompositeEntity): string[] {
  return filterRelevantActionTypes(item.collection_case_action_type_composites).map((a) => {
    return `payment_remark.collection_case.status.${a}`;
  });
}

export function getCompanyPaymentRemarkTableItem(item: CollectionCaseCompositeEntity): CompanyPaymentRemarkTableItem {
  return {
    uuid: item.uuid,
    date: item.collection_case.collection_reg_date,
    sum: item.collection_case.latest_debt_amount,
    creditor: item.collection_case.creditor_name,
    source: item?.collection_agency?.collection_agency_name || '',
    caseNumber: item.collection_case.case_number,
    statuses: getCompanyPaymentRemarkStatus(item),
  };
}

/*
 * Highcharts yAxis interval for quantity to avoid fractional values.
 */
export const QUANTITY_INTERVAL = 1;

/*
 * Highcharts is supposed to calculate proper yAxis intervals even when undefined is provided.
 * But for some reason it computes somewhat weird when we switch chart type.
 * This function computes intervals based on provided values.
 */
export function getSumInterval(items: Highcharts.PointOptionsObject[]): number | undefined {
  const values = (items || []).map((i) => i.y).filter((y): y is number => typeof y === 'number');
  const biggestNumber = Math.round(Math.max(...values));
  if (!Number.isInteger(biggestNumber)) {
    return undefined;
  }
  const divisions = biggestNumber > 20 ? 4 : 2;
  const delimiter = Number(
    [
      1,
      String(biggestNumber)
        .split('')
        .slice(0, -1)
        .map(() => 0)
        .join(''),
    ].join(''),
  );
  return Math.round(biggestNumber / divisions / delimiter) * delimiter;
}

export function getQuantityDataSeries(
  accountsNumberPipe: AccountsNumberPipe,
  collectionCaseCompositeEntities: CollectionCaseCompositeEntity[] = [],
  monthsRange: string[],
): Highcharts.PointOptionsObject[] {
  const dictByMonths = getValuesByMonths(collectionCaseCompositeEntities);

  return monthsRange.map((month) => {
    const value = dictByMonths[month]?.length ?? 0;
    return {
      y: value === 0 ? null : value,
      title: accountsNumberPipe.transform(value, 1),
    };
  });
}

export function getSumDataSeries(
  accountsNumberPipe: AccountsNumberPipe,
  collectionCaseCompositeEntities: CollectionCaseCompositeEntity[] = [],
  monthsRange: string[],
): Highcharts.PointOptionsObject[] {
  const dictByMonths = getValuesByMonths(collectionCaseCompositeEntities);
  return monthsRange.map((month) => {
    const value = calculateBarValue(dictByMonths[month] ?? []);
    return {
      y: value === 0 ? null : value,
      title: `${accountsNumberPipe.transform(value, 1)} NOK`,
    };
  });
}
